import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type {
  AppSettings,
  EditorConfig,
  EditorCreateRequest,
  PrizeConfigInput,
  StrategistCreateRequest,
  StrategistPatchRequest,
} from "@scandi/shared";
import { api } from "@/lib/api";
import { useFailureToast } from "@/hooks/use-failure-toast";

export function useSettings() {
  return useQuery({ queryKey: ["admin", "settings"], queryFn: api.admin.settings });
}

export function useUpdateSettings() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<AppSettings>) => api.admin.updateSettings(patch),
    onSuccess: (settings) => {
      queryClient.setQueryData(["admin", "settings"], settings);
      // Period length, pool and metric all change what the leaderboard shows.
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      queryClient.invalidateQueries({ queryKey: ["periods"] });
      queryClient.invalidateQueries({ queryKey: ["my-summary"] });
      toast.success(t("admin.sync.settingsSaved"));
    },
    onError,
  });
}

export function usePrizes() {
  return useQuery({ queryKey: ["admin", "prizes"], queryFn: api.admin.prizes });
}

/**
 * Create, change or remove one prize contest. One mutation for the three, since
 * each ends the same way: the admin list and the leaderboard's cards re-read.
 */
export function useSavePrize() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      change:
        | { kind: "create"; input: PrizeConfigInput }
        | { kind: "update"; id: number; patch: Partial<PrizeConfigInput> }
        | { kind: "delete"; id: number },
    ): Promise<void> => {
      if (change.kind === "create") await api.admin.createPrize(change.input);
      else if (change.kind === "update") await api.admin.updatePrize(change.id, change.patch);
      else await api.admin.deletePrize(change.id);
    },
    onSuccess: (_result, change) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "prizes"] });
      queryClient.invalidateQueries({ queryKey: ["prize-contests"] });
      toast.success(change.kind === "delete" ? t("bonus.prizes.deleted") : t("bonus.prizes.saved"));
    },
    onError,
  });
}

export function useEditors() {
  return useQuery({ queryKey: ["admin", "editors"], queryFn: api.admin.editors });
}

export function useUpdateEditor() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<Omit<EditorConfig, "id">> }) =>
      api.admin.updateEditor(id, patch),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "editors"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      // A tag edit re-reads every stored name, so an editor page open in another
      // tab is stale too, and the tracker links move with it.
      queryClient.invalidateQueries({ queryKey: ["editor"] });
      queryClient.invalidateQueries({ queryKey: ["creative"] });

      // Saying how many videos changed hands is the only way an admin can tell
      // a tag that works from one that matches nothing.
      if (!saved.moved) {
        toast.success(t("admin.sync.editorUpdated"));
      } else if (saved.moved.videos === 0) {
        toast.success(t("admin.editors.tagsSavedNoChange"));
      } else {
        toast.success(t("admin.editors.tagsSaved", { count: saved.moved.videos }));
      }
    },
    onError,
  });
}

/** Add an editor from just a name; tags and colour come back suggested. */
export function useCreateEditor() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (editor: EditorCreateRequest) => api.admin.createEditor(editor),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "editors"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      toast.success(t("admin.editors.created", { name: created.name }));
    },
    onError,
  });
}

/** The strategist roster as an admin edits it: every row, with linked accounts. */
export function useAdminStrategists() {
  return useQuery({ queryKey: ["admin", "strategists"], queryFn: api.admin.strategists });
}

function useInvalidateStrategists() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ["admin", "strategists"] });
    // The public roster, the payout board and the view-as picker all read it.
    queryClient.invalidateQueries({ queryKey: ["strategists"] });
    queryClient.invalidateQueries({ queryKey: ["strategist"] });
  };
}

export function useCreateStrategist() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const invalidate = useInvalidateStrategists();
  return useMutation({
    mutationFn: (strategist: StrategistCreateRequest) => api.admin.createStrategist(strategist),
    onSuccess: (created) => {
      invalidate();
      toast.success(t("admin.strategists.created", { name: created.name }));
    },
    onError,
  });
}

export function useUpdateStrategist() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const invalidate = useInvalidateStrategists();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: StrategistPatchRequest }) =>
      api.admin.updateStrategist(id, patch),
    onSuccess: () => {
      invalidate();
      toast.success(t("admin.strategists.saved"));
    },
    onError,
  });
}

export function useAccounts() {
  return useQuery({ queryKey: ["admin", "accounts"], queryFn: api.admin.accounts });
}

export function useSetAccountEnabled() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.admin.setAccountEnabled(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "accounts"] });
      toast.success(t("admin.sync.accountUpdated"));
    },
    onError,
  });
}

export function useSyncStatus() {
  return useQuery({
    queryKey: ["admin", "sync"],
    queryFn: api.admin.syncStatus,
    // A manual sync takes tens of seconds, so poll while this tab is open.
    refetchInterval: (query) => (query.state.data?.isRunning ? 3_000 : 30_000),
  });
}

export function useRunSync() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deep: boolean) => api.admin.runSync(deep),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "sync"] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      if (result.status === "success") {
        toast.success(t("admin.sync.synced", { count: result.rowsUpserted ?? 0 }));
      } else {
        // A skip is a safeguard doing its job, not an error.
        toast.info(result.reason ?? t("admin.sync.syncSkipped"));
      }
    },
    onError,
  });
}

export function useTrackerStatus() {
  return useQuery({ queryKey: ["admin", "tracker"], queryFn: api.admin.trackerStatus });
}

export function useSyncTracker() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.admin.syncTracker,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["admin", "tracker"] });
      // Links and launch dates are part of both payloads, so both go stale.
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });
      queryClient.invalidateQueries({ queryKey: ["editor"] });
      if (result.status === "success") {
        toast.success(t("admin.sync.imported", { count: result.rowsUpserted ?? 0 }));
      } else {
        toast.info(result.reason ?? t("admin.sync.importSkipped"));
      }
    },
    onError,
  });
}

export function useRevokeAllSessions() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  return useMutation({
    mutationFn: api.admin.revokeAllSessions,
    onSuccess: ({ revoked }) => {
      toast.success(t("admin.sync.signedOut", { count: revoked }));
      // The admin's own session was among them, so a reload lands on login.
      setTimeout(() => window.location.reload(), 900);
    },
    onError,
  });
}
